// skeleton/app/src/app/auth/oidc-config.service.ts

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LogoutServive } from '../logout/logout.service';
import { CookieService } from 'ngx-cookie-service';
import { Constants } from '../shared/constants';
import { BaseHttpService } from '../shared/services/basehttp.service';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SiteminderAuthService {
  constant: Constants = new Constants();
  private checkInterval: any;

  private readonly LOGOUT_FLAG_KEY = 'siteminder_force_logout';
  private logoutInProgress = false;

  constructor(
    private OauthService: OAuthService,
    private http: HttpClient,
    private router: Router,
    private logoutservice: LogoutServive,
    private cookieSevice: CookieService,
    private baseHttpService: BaseHttpService
  ) {}

  // Backward-compatible helper (some components expect this name)
  public isTokenValid(): boolean {
    try {
      return this.OauthService.hasValidAccessToken();
    } catch {
      return false;
    }
  }

  private isLogoutGuardOn(): boolean {
    try {
      const ss = sessionStorage.getItem(this.LOGOUT_FLAG_KEY);
      const ls = localStorage.getItem(this.LOGOUT_FLAG_KEY);
      return ss === '1' || ls === '1';
    } catch {
      return false;
    }
  }

  private setLogoutGuardOn(): void {
    try { sessionStorage.setItem(this.LOGOUT_FLAG_KEY, '1'); } catch {}
    try { localStorage.setItem(this.LOGOUT_FLAG_KEY, '1'); } catch {}
  }

  private clearLogoutGuard(): void {
    try { sessionStorage.removeItem(this.LOGOUT_FLAG_KEY); } catch {}
    try { localStorage.removeItem(this.LOGOUT_FLAG_KEY); } catch {}
  }

  revokeblacklisttoken(tokenOverride?: string): Observable<any> {
    const token =
      (tokenOverride || '').toString().trim() ||
      (this.OauthService.getAccessToken() || '').toString().trim() ||
      (sessionStorage.getItem('access_token') || '').toString().trim() ||
      (sessionStorage.getItem('siteminderAccessToken') || '').toString().trim();

    // Prevent 401 spam when token is missing
    if (!token) return of(null);

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post(environment.baseUrl + 'UserManagment/logout', {}, { headers });
  }

  /**
   * CRITICAL FIX:
   * Do NOT short-circuit here when logout guard is ON.
   * After SiteMinder logout JSP returns to app, we MUST still init OIDC and redirect to IdP login.
   */
  async initializeOAuthService() {
    const oidcConfig: AuthConfig = {
      loginUrl: environment.siteminder.loginUrl,
      clientId: environment.siteminder.clientId,
      redirectUri: environment.siteminder.redirectUri,
      issuer: environment.siteminder.issuer,
      tokenEndpoint: environment.siteminder.tokenEndpoint,
      userinfoEndpoint: environment.siteminder.userinfoEndpoint,
      revocationEndpoint: environment.siteminder.revocationEndpoint,

      requireHttps: true,
      responseType: 'code',
      scope: 'openid profile',
      showDebugInformation: true,
      sessionChecksEnabled: true,
      timeoutFactor: 0.5,
      clearHashAfterLogin: true,

      skipIssuerCheck: false,
      strictDiscoveryDocumentValidation: true,

      useSilentRefresh: false,
      useHttpBasicAuth: true,
      oidc: true,
    };

    this.OauthService.configure(oidcConfig);
    this.OauthService.setStorage(sessionStorage);
    this.OauthService.tokenValidationHandler = new JwksValidationHandler();

    try {
      await this.OauthService.loadDiscoveryDocumentAndTryLogin();

      // If token is valid, normal flow
      if (this.OauthService.hasValidAccessToken()) {
        this.clearLogoutGuard();
        this.startTokenRefreshInterval();
        this.postLoginAction();
        return;
      }

      /**
       * If NOT logged in:
       * ALWAYS send user to IdP login.
       * This is what fixes the "blank/loading after logout" because SiteMinder logout JSP returns to app
       * without honoring TARGET, and your app must initiate login again.
       */
      this.OauthService.initCodeFlow();
    } catch (error) {
      console.error('SiteMinder initialization failed', error);
    }
  }

  private postLoginAction(): void {
    this.router.navigate(['/odp/signin-oidc-bp']);
  }

  loginoidc() {
    setTimeout(() => this.loggingintooidc(), 500);
  }

  async loggingintooidc() {
    // If not logged in, force login flow
    if (!this.OauthService.hasValidAccessToken()) {
      this.OauthService.initLoginFlow();
    }
  }

  getValidAccessToken() {
    return this.OauthService.getAccessToken();
  }

  getValidIdToken() {
    return this.OauthService.getIdToken();
  }

  getValiClaims() {
    return this.OauthService.getIdentityClaims();
  }

  isLoggedIn() {
    return this.OauthService.hasValidAccessToken();
  }

  private revokeTokenDirect(token: string, tokenTypeHint: 'access_token' | 'refresh_token'): void {
    try {
      const endpoint = (environment?.siteminder?.revocationEndpoint || '').toString().trim();
      const clientId = (environment?.siteminder?.clientId || '').toString().trim();
      if (!endpoint || !token || !clientId) return;

      const body = new HttpParams()
        .set('token', token)
        .set('token_type_hint', tokenTypeHint)
        .set('client_id', clientId);

      const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

      this.http.post(endpoint, body.toString(), { headers }).subscribe({ next: () => {}, error: () => {} });
    } catch {}
  }

  async logout() {
    // Prevent re-entry
    if (this.logoutInProgress) return;
    this.logoutInProgress = true;

    // Keep flag (fine), but it MUST NOT block initializeOAuthService anymore
    this.setLogoutGuardOn();

    try { if (this.checkInterval) clearInterval(this.checkInterval); } catch {}
    try { this.OauthService.stopAutomaticRefresh(); } catch {}

    const accessToken =
      (this.OauthService.getAccessToken() || '').toString().trim() ||
      (sessionStorage.getItem('access_token') || '').toString().trim() ||
      (sessionStorage.getItem('siteminderAccessToken') || '').toString().trim();

    const refreshToken =
      ((this.OauthService as any)?.getRefreshToken?.() || '').toString().trim() ||
      (sessionStorage.getItem('refresh_token') || '').toString().trim();

    try { if (accessToken) this.revokeTokenDirect(accessToken, 'access_token'); } catch {}
    try { if (refreshToken) this.revokeTokenDirect(refreshToken, 'refresh_token'); } catch {}

    try {
      if (accessToken) {
        this.revokeblacklisttoken(accessToken).subscribe({ next: () => {}, error: () => {} });
      }
    } catch {}

    try { (this.OauthService as any).logOut?.(true); } catch {}

    // Clear storage first
    try { sessionStorage.clear(); } catch {}
    try { localStorage.clear(); } catch {}

    // Put guard back in SESSION only (optional)
    try { sessionStorage.setItem(this.LOGOUT_FLAG_KEY, '1'); } catch {}

    try { this.clearcookies(); } catch {}

    // Redirect to IdP logout page
    try {
      const logoutJsp = (environment?.siteminder?.logoutUrl || '').toString().trim();

      // Target back to APP ROOT (since SiteMinder logout JSP seems to redirect to root anyway)
      const targetUrl = `${window.location.origin}/`;
      const target = encodeURIComponent(targetUrl);

      if (logoutJsp) {
        window.location.replace(`${logoutJsp}?TARGET=${target}`);
        return;
      }
    } catch {}

    // Fallback
    try { window.location.replace(`${window.location.origin}/`); } catch {}
  }

  clearcookies() {
    this.cookieSevice.deleteAll('/');
  }

  public startTokenRefreshInterval(): void {
    if (this.checkInterval) clearInterval(this.checkInterval);

    this.checkInterval = setInterval(async () => {
      const idToken = this.getValidIdToken();
      if (!idToken) return;

      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const expiry = payload.exp * 1000;
        const timeLeft = expiry - Date.now();

        if (timeLeft < 3 * 60 * 1000 && timeLeft > 0) {
          this.OauthService.refreshToken().then((result) => {
            if (result) {
              sessionStorage.setItem('siteminderAccessToken', this.getValidAccessToken());
              sessionStorage.setItem('siteminderIdToken', this.getValidIdToken());
            } else {
              this.logoutservice.setCognitoUserLoginStatus(false);
              this.router.navigate(['/', 'login']);
            }
          });
        }
      } catch {
        // If token parsing fails, do nothing here
      }
    }, 60000);
  }
}